// The axis: one list, sorted by time, past above the kreska and future below
// (SPEC-redesign §4.5, §5.4, §5.5). Pure, so it is tested without a browser.
//
// Everything a resident can read on the rail is decided here: which rows exist,
// which of them a silenced voice has collapsed into a thin line, where a source
// puts its complaint, and which items the circle may draw. `src/axis/axis-view.ts` only paints it.
import type { Snapshot } from '../data/data.ts';
import { linesLabel } from '../shared/alerts.ts';
import { clockLabel, dateTimeLabel, dayLabel, poznanOffset, poznanWall } from '../shared/format.ts';
import { distanceLabel, haversine } from '../shared/geo.ts';
import type { AxisRow, Item, Voice } from '../shared/model.ts';
import type { ViewState } from '../state/viewstate.ts';
import { byDistance } from './order.ts';
import { type Note, nothingNotes, plural, runLabel, sourceNote, ztmGap } from './voices.ts';

export type AxisInput = {
  snapshot: Snapshot;
  view: ViewState;
  /** The centre distances are measured from. */
  centre: { lat: number; lon: number };
  /** `Date.now()` in the caller, so the builder stays pure and testable. */
  now: number;
};

export type Axis = {
  /**
   * The rail, in the order `view.order` asks for. Mute runs sit at the place of
   * the rows they replace.
   */
  rows: AxisRow[];
  /** The items that get a mark, in axis order — the circle's DOM order follows this. */
  marked: Item[];
  /** `marked` out to 1,6 × the radius: what a gesture may SHOW, never what is drawn at rest (§4.1). */
  nearby: Item[];
  /**
   * The three sets the ring divides the rail's own items into (§2.1, §2.2).
   * Their union is `onTheRail` — what would have a row before the radius is
   * asked about — so `placed.length` is „w promieniu N" and `beyond.length` is
   * „poza M", and nothing is counted twice.
   *
   * `beyond` is also the array V4 draws as dots (§3): one filter behind both,
   * though a dot off the visible map is clipped and the count is not.
   */
  placed: Item[];
  /**
   * The city-wide rows with no `lat`/`lon` at all — a ZTM notice about a whole
   * line, an Aquanet report with no address. They belong to every centre, so
   * they belong at every radius (§2.1), and they are in no ring, so they are
   * counted in neither number.
   */
  placeless: Item[];
  /** Positioned, on the rail, and outside the radius. Nearest first. */
  beyond: Item[];
};

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Whether this moment and the clock are the same „teraz" (§7.2). One minute
 * either side, and the ONE place that window is written down.
 *
 * Not exported: both callers are in this file, and the two things that must
 * agree — the row's label and the band's boundary — are what is exported.
 */
const readsNow = (at: number, now: number) => Math.abs(at - now) < MINUTE;

/**
 * Whether a row belongs at „teraz" or after it — which is to say, below the
 * kreska rather than above it (§5.3).
 *
 * Defined by the label, not beside it. The band is the boundary the ROWS name,
 * so „below the band" is „this row reads „teraz"" plus „this row is ahead of
 * the clock" — one predicate, called by both, rather than two expressions that
 * happen to spell the same constant and drift the first time one is touched.
 *
 * The relation it gives, exactly: every row labelled „teraz" is below the band,
 * and nothing above the band is labelled „teraz". The label is two-sided and
 * this is one-sided, which is the whole difference between them.
 *
 * A vehicle's `at` is the moment its position was fixed, always a few seconds
 * behind the clock, so `at <= now` would put every vehicle on the road above
 * the band — and a rule counting whole minutes moved them across it and back as
 * the seconds ticked (issue 160, G1).
 */
export const atNowOrAfter = (at: number, now: number) => readsNow(at, now) || at >= now;

/** „teraz", „+4 min", „−6 h 30", „−3 d" — §7.2, with the Polish minus sign. */
export function relativeTime(at: number, now: number): string {
  if (readsNow(at, now)) return 'teraz';
  const delta = at - now;
  const ahead = delta >= 0;
  const abs = Math.abs(delta);
  const sign = ahead ? '+' : '−';
  if (abs < HOUR) return `${sign}${Math.round(abs / MINUTE)} min`;
  if (abs < DAY) {
    const hours = Math.floor(abs / HOUR);
    const minutes = Math.round((abs % HOUR) / MINUTE);
    return minutes ? `${sign}${hours} h ${minutes}` : `${sign}${hours} h`;
  }
  return `${sign}${Math.round(abs / DAY)} d`;
}

/** „930 m", „2,0 km", „1,5 km?" when we do not trust the fix, „—" with no location. */
export function distance(metres: number | null, uncertain: boolean): string {
  if (metres == null) return '—';
  const label = distanceLabel(metres);
  return uncertain ? `${label}?` : label;
}

/** „15.09, 09:15" — an entry from another day says which day (§7.2). */
function stamp(at: number, now: number): string {
  // Compared in Poznań's day, not the machine's: at 23:30 UTC a report from
  // this evening is already „tomorrow" in a zone we are not in.
  return dayLabel(at) === dayLabel(now) ? clockLabel(at) : dateTimeLabel(at);
}

/**
 * The second line of a row, finished. `src/data.ts` builds an `Item.sub` from
 * the wire and stops there — it holds the stem („Zaczyna się", „Zgłoszono") and
 * the place, because the clock a row reads by belongs to the axis, which is the
 * only thing that knows what „now" is. Here the two are joined into the sentence
 * the prototype shows.
 */
export function subLine(item: Item, now: number): { a: string; b: string } {
  if (item.kind === 'vehicle') {
    // „+4 min · ◦ 5 s". A feed that said nothing about the schedule leaves
    // `sub.a` empty (`src/data.ts`), and a bare „◦ 20 min" would read as if the
    // tram were fine; the prototype says „◦ 20 min · opóźnienie nieznane".
    if (item.delayMin == null) return { a: '', b: `${item.sub.b} · opóźnienie nieznane` };
    return { a: item.sub.a, b: item.sub.b };
  }
  if (item.kind === 'event') return { a: `${item.sub.a} o ${clockLabel(item.at)}`, b: item.sub.b };
  if (item.kind === 'notice') return { a: '', b: `Komunikat ZTM Poznań${linesLabel(item.lines ?? [])}` };
  return {
    a: '',
    b: `${item.sub.a} ${stamp(item.at, now)}${item.position ? '' : ', bez lokalizacji na mapie'}`,
  };
}

/** The same threshold `delayLabel` uses: under two minutes is not a callout. */
const DELAYED_MIN = 2;

/**
 * A row reads by the minute, and a line has many vehicles on the road: at rush
 * hour a dozen of them are all „teraz". Their order has to come from something
 * the resident can see — „Pierwszy wiersz pod nią to opóźniony pojazd" (the
 * brief, Decision 38) — so, among rows that read the same minute:
 *
 *   0  a vehicle running late (`delayMin >= 2`), the largest delay first
 *   1  a vehicle we know is on time or early
 *   2  a vehicle whose delay the feed never told us
 *   3  everything that is not a vehicle
 *
 * and inside each of those, the nearest to the centre first.
 */
function rank(item: Item): number {
  if (item.kind !== 'vehicle') return 3;
  if (item.delayMin == null) return 2;
  return item.delayMin >= DELAYED_MIN ? 0 : 1;
}

const minute = (at: number) => Math.round(at / 60_000);

/**
 * The instant the axis calls „teraz". Normally the caller's clock; with the
 * centre moved in time (`teraz=15:00`) it is that hour **in Poznań** on the
 * caller's Poznań day, so the builder stays pure and the tick row can still
 * mark the real now.
 *
 * Not `setHours`: that reads the machine's zone, so on a laptop in New York
 * „o 15:00" would have been 15:00 there — a different afternoon entirely.
 *
 * The two hours that do not behave are undefined rather than chosen, and one
 * rule describes both: the offset in force at the caller's instant is applied,
 * then the offset in force at the instant that produces — so for those two
 * hours the answer depends on **when you ask**. „teraz=02:30" on the March
 * night names an hour Poznań never reaches, and comes back as 01:30 CET asked
 * before the change, 03:30 CEST asked after; on the October night it names an
 * hour Poznań passes twice, and comes back as the first pass before the
 * change, the second after (#170, F1/G1).
 */
export function axisNow(view: ViewState, now: number): number {
  if (!view.at) return now;
  const [h, m] = view.at.split(':').map(Number);
  if (h == null || m == null || !Number.isFinite(h) || !Number.isFinite(m)) return now;
  const today = poznanWall(now);
  const wall = Date.UTC(today.year, today.month - 1, today.day, h, m);
  // `wall` is that wall time read as if it were UTC; stepping back by the
  // offset gives the real instant. Twice, because the offset a few hours away
  // can differ from the offset now — once a year, on the night the clocks go.
  const once = wall - poznanOffset(now);
  return wall - poznanOffset(once);
}

/**
 * How far either side of „teraz" an event still earns a tag on the disc.
 *
 * The real calendar is not the prototype's four entries: a working day puts
 * thirty-odd inside 3 km, and thirty `PL` tags are not a picture of the city,
 * they are a wall in front of it. The axis is the list — **every** event of the
 * day keeps its row — and the circle answers the narrower question the disc is
 * actually asking, which is what is going on around here around now.
 *
 * Interim, and the owner's to confirm: §11 had deferred the clustering and
 * overflow rules for many marks in one spot, and this is the first half of that
 * question arriving early.
 */
const MARK_STARTED_MS = 2 * HOUR;
const MARK_AHEAD_MS = 3 * HOUR;

/**
 * Whether this item is close enough to „teraz" to be worth a mark. Only events
 * are asked: a vehicle is where it is *now* by definition, and a water fault
 * lasts until somebody fixes it. The open row is always drawn whatever its
 * hour, so opening anything still lights its place on the wheel.
 */
function nearNow(item: Item, view: ViewState, now: number): boolean {
  if (item.kind !== 'event') return true;
  if (view.selected === item.id) return true;
  const delta = item.at - now;
  return delta > 0 ? delta <= MARK_AHEAD_MS : -delta <= MARK_STARTED_MS;
}

/** Why a row is not on the axis in its own right. `null` = it is. */
type Silence = 'muted' | 'solo' | null;

function silenced(item: Item, view: ViewState): Silence {
  // The line lens steps everything outside the line back the same way solo does:
  // the rows collapse into a „poza solo" run (measured in the prototype, §9.6).
  if (view.onlyLine && !(item.lines ?? []).includes(view.onlyLine)) return 'solo';
  if (view.solo) return item.voice === view.solo ? null : 'solo';
  return view.muted.includes(item.voice) ? 'muted' : null;
}

/**
 * A silenced voice says nothing at all, complaint included (§7.2). Under a line
 * lens that also covers a voice with nothing inside the lens: its rows are
 * stepped back into a run, and a sentence left behind by rows that are no longer
 * there would be the loudest thing on the axis.
 */
const voiceSilenced = (voice: Voice, view: ViewState, inLens: Set<Voice>): boolean => {
  if (view.solo) return view.solo !== voice;
  if (view.muted.includes(voice)) return true;
  return view.onlyLine != null && !inLens.has(voice);
};

/**
 * Two days, the same threshold §4.4 dims a row at. Taken from `at`, never from
 * `weight`: the weight is how we draw the age, not the age.
 */
const OLD_MS = 48 * 60 * 60 * 1000;

export type Fold = {
  /** The rows behind the „starsze" line, in time order. */
  older: AxisRow[];
  /** Everything else, in time order. */
  rest: AxisRow[];
  /** „starsze · 4 wpisy", or `''` when nothing folds. */
  label: string;
};

/**
 * The old past folds into one line at the head of the axis.
 *
 * On real data the rail opens on notices published eleven and twelve days ago —
 * seventy-odd rows, with „teraz" thousands of pixels below the fold. The
 * prototype's own template declares a row kind for this (`showEarlier`,
 * „starsze") that its `build()` never produces, because its demo set has
 * nothing old enough; this finishes what the drawing started.
 *
 * Nothing disappears: the line is a button, and it opens in place.
 *
 * Two things never fold, however old they are. A ZTM notice naming one of the
 * resident's own lines — a detour published twelve days ago is still today's
 * news for the tram they are waiting for — and whatever is open, because a
 * resident who followed a link to a row must find that row.
 */
export function foldOld(rows: AxisRow[], view: ViewState, now: number): Fold {
  const exempt = (item: Item) =>
    item.id === view.selected ||
    (item.kind === 'notice' && (item.lines ?? []).some((l) => view.lines.includes(l)));
  const old = (row: AxisRow) => row.kind === 'item' && now - row.item.at > OLD_MS && !exempt(row.item);

  const older = rows.filter(old);
  if (!older.length) return { older, rest: rows, label: '' };
  return {
    older,
    rest: rows.filter((r) => !old(r)),
    label: `starsze · ${older.length} ${plural(older.length, 'wpis', 'wpisy', 'wpisów')}`,
  };
}

/**
 * What the circle draws. A mark means "this is inside the circle": an item
 * further away than the radius, or with no location at all, is not drawn (§4.5,
 * D-4). A silenced voice draws nothing; the line lens only dims, so those marks
 * stay and S1 fades them. Asked twice — for the radius, and for 1,6 × it, which
 * is `nearby` (§4.1, `./remeasure.ts`).
 */
function marks(sorted: Item[], view: ViewState, metres: Map<string, number | null>, now: number): Item[] {
  return sorted.filter((item) => {
    if (view.solo ? view.solo !== item.voice : view.muted.includes(item.voice)) return false;
    if (!nearNow(item, view, now)) return false;
    const d = metres.get(item.id);
    return d != null && d <= view.radius;
  });
}

/**
 * What the ring keeps, what it leaves out and what is in no ring at all — the
 * list is the radius (§2.1, amending D-4). The same predicate that has always
 * gated a MARK now gates a ROW: an item further away than the radius is not on
 * the list in any form, and the placeless items are kept rather than dropped,
 * because something that belongs to the whole city belongs to every centre.
 *
 * Asked of `onTheRail` and not of `marked`: `marked` ignores `view.lines`, so
 * it holds all ~247 vehicles and would make „poza" read 247 on a quiet
 * afternoon (§2.2). It is asked BEFORE the mixer, too — a voice the resident
 * silenced still has items in the ring, and „w promieniu 12" is about the city
 * rather than about the switches.
 *
 * `onTheList` is `onTheRail`'s own order with what is out of reach taken out of
 * it, and not `placed` and `placeless` concatenated, which would put every
 * city-wide row after every positioned one.
 */
function ring(onTheRail: Item[], metres: Map<string, number | null>, radius: number) {
  const placed: Item[] = [];
  const placeless: Item[] = [];
  const beyond: Item[] = [];
  for (const item of onTheRail) {
    const d = metres.get(item.id);
    if (d == null) placeless.push(item);
    else if (d <= radius) placed.push(item);
    else beyond.push(item);
  }
  const away = (i: Item) => metres.get(i.id) ?? Number.POSITIVE_INFINITY;
  beyond.sort((a, b) => away(a) - away(b));
  const out = new Set(beyond.map((i) => i.id));
  return { placed, placeless, beyond, onTheList: onTheRail.filter((i) => !out.has(i.id)) };
}

export function buildAxis(input: AxisInput): Axis {
  const { snapshot, view, centre, now: realNow } = input;
  const now = axisNow(view, realNow);
  const moved = view.at != null;

  // Measured once: the comparator, the distance column and the radius all want it.
  const metres = new Map<string, number | null>();
  for (const item of snapshot.items) {
    metres.set(item.id, item.position ? haversine(centre, item.position) : null);
  }
  const away = (item: Item) => metres.get(item.id) ?? Number.POSITIVE_INFINITY;

  // With the centre moved in time we do not know where anything was driving, and
  // we say so rather than drawing this minute's positions under another hour.
  const drawable = moved ? snapshot.items.filter((i) => i.kind !== 'vehicle') : snapshot.items;

  const sorted = drawable.toSorted((a, b) => {
    const byRank = rank(a) - rank(b);
    // The magnitude only orders the late ones. Two vehicles under the threshold
    // are equally not-late, and a minute of it must not jump the nearer row.
    const byDelay = byRank === 0 && rank(a) === 0 ? (b.delayMin ?? 0) - (a.delayMin ?? 0) : 0;
    return minute(a.at) - minute(b.at) || byRank || byDelay || away(a) - away(b) || a.id.localeCompare(b.id);
  });

  // The rail is the resident's own afternoon: their lines, and everything that
  // is not a vehicle. Every other tram and bus in the city is a grey dot on the
  // circle and gets no row — that is what makes those dots decoration rather
  // than something the keyboard must reach (D-f), and what lets the kreska's
  // „Linie 16, 174, 2 pojazdy" agree with the rows underneath it (§5.3).
  // Confirmed in the prototype: with no lines saved its rail has no vehicle
  // rows, although its vehicles are in the data and on the circle.
  //
  // The line lens is the second door in: `#tylko=160` from an old link asks for
  // that line's vehicles whether or not it is saved, so a link written by the
  // old app still opens the line it names (§9.3).
  const asked = (item: Item) => {
    const lines = item.lines ?? [];
    return (
      lines.some((l) => view.lines.includes(l)) || (view.onlyLine != null && lines.includes(view.onlyLine))
    );
  };
  const onTheRail = sorted.filter((i) => i.kind !== 'vehicle' || asked(i));

  const { placed, placeless, beyond, onTheList } = ring(onTheRail, metres, view.radius);

  const side = (at: number): 'past' | 'future' => (at <= now ? 'past' : 'future');
  const rows: AxisRow[] = [];

  // A silenced voice leaves one thin line in the place of the rows it had, and a
  // run never crosses the kreska — the past and the future are two readings, not
  // one (§5.5). So runs accumulate per side and per reason, and are flushed when
  // a row that is not silenced comes between them.
  let run: { side: 'past' | 'future'; reason: 'muted' | 'solo'; items: Item[] } | null = null;
  const flush = () => {
    if (!run) return;
    const voices: Voice[] = [];
    for (const i of run.items) if (!voices.includes(i.voice)) voices.push(i.voice);
    const first = run.items[0];
    rows.push({
      kind: 'muteRun',
      side: run.side,
      at: first?.at ?? now,
      // A run stands where the first row it collapsed stands, in either order (§2.3).
      dist: first ? (metres.get(first.id) ?? null) : null,
      voices,
      count: run.items.length,
      reason: run.reason,
      label: runLabel(run.items, voices, run.reason),
    });
    run = null;
  };

  for (const item of onTheList) {
    const reason = silenced(item, view);
    const itemSide = side(item.at);
    if (reason) {
      if (run && (run.side !== itemSide || run.reason !== reason)) flush();
      run ??= { side: itemSide, reason, items: [] };
      run.items.push(item);
      continue;
    }
    flush();
    rows.push({
      kind: 'item',
      item,
      side: itemSide,
      // Relative by default; with the centre moved in time every row carries the
      // clock it actually happens at, because „+1 h" from a borrowed hour is a
      // riddle (§5.4).
      time: moved ? clockLabel(item.at) : relativeTime(item.at, now),
      distance: distance(metres.get(item.id) ?? null, item.uncertain),
    });
  }
  flush();

  // A voice's own sentence sits where that voice is: beside its rows when all of
  // them are behind us, and at „teraz" otherwise, where a resident reading the
  // kreska will meet it.
  // Which voices still have a row of their own inside the lens.
  const inLens = new Set<Voice>(onTheRail.filter((i) => !silenced(i, view)).map((i) => i.voice));
  const hushed = (voice: Voice) => voiceSilenced(voice, view, inLens);

  const notes: AxisRow[] = [];
  const noteAt = (voice: Voice): number => {
    const its = onTheRail.filter((i) => i.voice === voice);
    const last = its.at(-1);
    return its.length && last && last.at <= now ? last.at : now;
  };
  const push = (note: Note | null) => {
    if (!note || hushed(note.voice)) return;
    notes.push({ kind: 'note', at: noteAt(note.voice), ...note });
  };

  if (snapshot.serverReachable) {
    for (const voice of ['ztmk', 'pl', 'aq'] as const) {
      push(sourceNote(voice, snapshot.health[voice], now));
    }
    // A working source that simply has nothing to say says that, and is not left
    // to look broken (§9.3 rows 28/36).
    const quiet = (voice: Voice) => snapshot.health[voice].state === 'ok';
    const pl = onTheRail.filter((i) => i.voice === 'pl');
    if (quiet('pl')) {
      if (!pl.length)
        push({
          voice: 'pl',
          text: 'Dziś nie ma już wydarzeń w kalendarzu poznan.pl.',
          tone: 'ink',
          connecting: false,
        });
      else if (!pl.some((i) => i.at > now))
        push({ voice: 'pl', text: 'Dziś nic się już nie zaczyna.', tone: 'ink', connecting: false });
    }
    // „Nothing to report" is two facts, each dated by the source that reports
    // it (#75) and said only while its voice is audible (D-11).
    const nothingFrom = (voice: Voice) =>
      quiet(voice) && !hushed(voice) && !onTheRail.some((i) => i.voice === voice);
    const silent = (['aq', 'ztmk'] as const).filter(nothingFrom);
    for (const note of nothingNotes(silent, snapshot.health)) push(note);
    if (moved && !hushed('ztm')) {
      notes.push({
        kind: 'note',
        voice: 'ztm',
        at: now,
        text: `Pojazdów o ${view.at} jeszcze nie znamy. Wróć do „teraz”, żeby je zobaczyć.`,
        tone: 'ink',
        connecting: false,
      });
    }
  }

  // „brak połączenia z serwerem" speaks for every voice, so muting ZTM does not
  // silence it — it is a statement about the server, not about ZTM (§9.3 row 20).
  const gap = !snapshot.serverReachable || !hushed('ztm') ? ztmGap(snapshot, realNow, moved) : null;
  const extra: AxisRow[] = [...notes, ...(gap ? [gap] : [])];
  // The tick marks the real „teraz" once the axis is reading another hour.
  if (moved) extra.push({ kind: 'tick', at: realNow, label: `teraz ${clockLabel(realNow)}` });

  // The extras take their place by the clock like everything else, and a row that
  // reads the same minute as one of them keeps the lead: a complaint belongs
  // under what it is about.
  for (const row of extra) {
    const at = 'at' in row ? row.at : now;
    const before = rows.findIndex((r) => {
      const other = 'at' in r ? r.at : now;
      return minute(other) > minute(at);
    });
    if (before === -1) rows.push(row);
    else rows.splice(before, 0, row);
  }

  return {
    rows: view.order === 'odleglosc' ? byDistance(rows, metres) : rows,
    marked: marks(sorted, view, metres, now),
    nearby: marks(sorted, { ...view, radius: view.radius * 1.6 }, metres, now),
    placed,
    placeless,
    beyond,
  };
}
